import { ChangeEvent, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DataTable,
  DataTablePageEvent,
  DataTableRowClickEvent,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { useInstitutions } from "../../api/queries/institutions/useInstitutions";
import { Institution } from "../../api/types/institution";
import { Button } from "primereact/button";
import {
  formatDateTime,
  parseQueryString,
  toQueryString,
} from "../../common/utils";
import { InputText } from "primereact/inputtext";
import styles from "./styles.module.css";
import { useDebounce } from "../../common/useDebounce";
import { userContext } from "../../context/user";

export default function InstitutionList() {
  const navigate = useNavigate();
  const limit = 20;
  const queryParams = parseQueryString(useLocation().search);

  const { isAdmin } = useContext(userContext);

  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [sortField, setSortField] = useState(queryParams.sortField);
  const [sortOrder, setSortOrder] = useState(queryParams.sortOrder);
  const [search, setSearch] = useState<string>(queryParams.search || "");
  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) =>
    setSearch(event.target.value);
  const searchDebounced = useDebounce(search, 500);

  const onPage = ({ first }: DataTablePageEvent) => setOffset(first);
  const onSort = (event: any) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder);
  };

  const { data: institutions, isLoading } = useInstitutions({
    limit,
    offset,
    search: searchDebounced,
  });

  const onRowClick = (event: DataTableRowClickEvent) =>
    navigate(`/institutions/${event.data.id}`);

  const tName = (row: Institution) => row.name;

  const tWeb = (row: Institution) => (
    <a href={row.web} target="_blank" rel="noreferrer">
      {row.web}
    </a>
  );

  const tIsApproved = (row: Institution) => (
    <i className={row.is_approved ? "fa fa-check" : "fa fa-times"} />
  );

  const tCreatedAt = (row: Institution) => formatDateTime(row.created_at);

  useEffect(() => {
    const queryParamsStr = toQueryString({
      limit: 20,
      offset,
      search: searchDebounced,
      sortField,
      sortOrder,
    });
    navigate({ search: queryParamsStr });
  }, [offset, navigate, sortField, sortOrder, searchDebounced]);

  useEffect(() => {
    if (institutions) {
      setTotal(institutions.data.total_count);
    }
  }, [institutions]);

  return (
    <div className={"col-12"}>
      <div className="flex py-3">
        <h1 className="m-0">Institution</h1>

        <div className="flex gap-1 ml-auto">
          {isAdmin() && (
            <Button
              onClick={() => navigate("/institutions/approve")}
              className="p-button p-button-main p-button-sm"
              label="Approve"
            />
          )}

          <Button
            onClick={() => navigate("/institutions/new")}
            className="p-button p-button-main p-button-sm"
            label="Add new"
          />
        </div>
      </div>

      <div className="mb-3">
        <InputText
          value={search}
          onChange={onSearchChange}
          placeholder="Search"
          className={styles.searchInput}
        />
      </div>

      <DataTable
        emptyMessage={"No records"}
        paginator={true}
        lazy={true}
        rows={20}
        first={offset}
        totalRecords={total}
        loading={isLoading}
        sortField={sortField}
        sortOrder={sortOrder}
        onPage={onPage}
        onSort={onSort}
        value={institutions?.data.results || []}
        className="p-datatable-sm p-datatable-pointer"
        onRowClick={onRowClick}
        stripedRows={true}
      >
        <Column header={"Name"} body={tName} />

        <Column header={"Web"} body={tWeb} />

        <Column header={"Approved"} body={tIsApproved} />

        <Column header={"Created at"} body={tCreatedAt} />
      </DataTable>
    </div>
  );
}
